import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { FlagOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { MOBILE_GUTTER, PHONE_MAX_WIDTH, mediaPhone } from "@/shared/theme/breakpoints";
import type { Market } from "@/shared/types/campaign/types";
import { Icon } from "@/shared/components/Icon/Icon";
import { LoadingWrapper } from "@/shared/components";
import { useEngagementTermsForm } from "../../hooks/engagement-terms-tab/useEngagementTermsForm";
import { useEngagementDeliverables } from "../../hooks/engagement-terms-tab/useEngagementDeliverables";
import { useEngagementTermsLifecycle } from "../../hooks/engagement-terms-tab/useEngagementTermsLifecycle";
import { useEngagementCreators } from "../../hooks/engagement-terms-tab/useEngagementCreators";
import {
	snapshotFromResponse,
	snapshotToPayload,
	type CetSnapshot,
} from "../../hooks/engagement-terms-tab/engagementTermsState";
import {
	getEngagementTermsCreatorKey,
	loadCachedCreatorVersions,
	type EngagementTermsCache,
} from "../../hooks/engagement-terms-tab/engagementTermsCache";
import { EngagementCreatorsPanel, EngagementTermsHeader } from "./EngagementCreatorsPanel";
import { SendEngagementTermsModal } from "./SendEngagementTermsModal";
import { DeliverablesSection } from "../engagement-terms/DeliverablesSection";
import { EngagementTermsEmptyState } from "../engagement-terms/EngagementTermsEmptyState";
import { EngagementTermsEditorModals } from "../engagement-terms/EngagementTermsEditorModals";
import { EngagementTermsFooter } from "../engagement-terms/EngagementTermsFooter";
import { ExclusivitySection } from "../engagement-terms/ExclusivitySection";
import { getApiErrorMessage } from "../engagement-terms/constants";
import { CreatorFeeSection, EffectiveDatesSection } from "../engagement-terms/TermsOverviewSections";
import type { RegisterValidationTarget } from "../engagement-terms/types";
import { engagementTermsAPI, type CampaignEngagementTerms } from "@/api/engagementTerms";
import { formatBudgetValue } from "@/shared/utils/budget";

const LOCAL_VERSION_PREFIX = "local-cet-version:";

const EngagementTermsLoading = styled(LoadingWrapper)`
	position: absolute;
	inset: 0;
	z-index: 20;
	height: 100%;
`;

const EngagementTermsLoadingSurface = styled.div`
	width: 100%;
	height: 100%;
	min-height: 300px;
	background: #eaeefd;
	border-radius: 16px;
`;

/*
 * Phones show one pane at a time: the market and creator list, or the terms form for the chosen
 * creator. Both stay mounted so an in-progress draft survives switching back to the list.
 */
const Shell = styled.div<{ $phoneView: "list" | "detail" }>`
	position: relative;
	display: grid;
	grid-template-columns: 317px minmax(620px, 1fr);
	gap: 16px;
	height: 100%;
	min-height: 0;
	font-family: var(--font-family-app);

	@media (max-width: 1050px) {
		grid-template-columns: 260px minmax(560px, 1fr);
	}

	${({ $phoneView }) => mediaPhone`
		grid-template-columns: minmax(0, 1fr);
		gap: 0;
		/* The list keeps the page gutter; the terms form runs edge to edge. */
		padding: 0 ${$phoneView === "list" ? MOBILE_GUTTER : 0}px;

		& > aside {
			display: ${$phoneView === "list" ? "block" : "none"};
		}

		& > section {
			display: ${$phoneView === "detail" ? "flex" : "none"};
		}
	`}
`;

const Title = styled.div`
	color: #666;
	font-size: 16px;
	font-weight: 600;
`;

const FormCard = styled.section`
	position: relative;
	display: flex;
	min-width: 0;
	min-height: 0;
	height: 100%;
	flex-direction: column;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.04);
	overflow: hidden;

	/*
	 * Phones: no card chrome, and the whole form scrolls as one: the creator header and version row stay
	 * pinned, the footer (status and actions) sits at the end of the terms instead of docked over them.
	 */
	${mediaPhone`
		border-radius: 0;
		box-shadow: none;
		overflow-y: auto;
		overscroll-behavior: contain;
	`}
`;

const SaveMarketOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 30;
	display: grid;
	place-items: center;
	padding: 24px;
	background: rgba(243, 245, 255, 0.94);
	backdrop-filter: blur(2px);
`;

const SaveMarketMessage = styled.div`
	display: flex;
	max-width: 420px;
	align-items: center;
	flex-direction: column;
	gap: 10px;
	color: #303030;
	text-align: center;

	.anticon {
		margin-bottom: 8px;
		color: #999;
		font-size: 26px;
	}

	strong {
		color: #3a0ca3;
		font-size: 16px;
	}

	span {
		font-size: 14px;
		line-height: 1.45;
	}
`;

const ScrollBody = styled.div`
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 16px 24px;

	${mediaPhone`
		flex: none;
		overflow: visible;
		padding: 0 16px;
	`}
`;

const LockableTerms = styled.fieldset<{ $disabled: boolean; $locked: boolean }>`
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
	${(props) => props.$disabled && "pointer-events: none;"}
	${(props) => props.$locked && ".cet-required-mark { display: none; }"}
`;

const InfoBanner = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 56px;
	padding: 16px;
	border: 1px solid #cdd4f6;
	border-radius: 16px;
	background: #f3f5ff;
	color: #303030;
	font-size: 14px;

	${mediaPhone`
		margin-top: 12px;
	`}
`;

interface Props {
	campaignId: string | null;
	markets: Market[];
	cache: EngagementTermsCache;
	onMarketTermsSaved?: () => void;
	/** Market country name to select on mount instead of the first market (e.g. a deep link from the CET gate). */
	initialCountry?: string | null;
}

export const EngagementTermsTab: React.FC<Props> = ({
	campaignId,
	markets,
	cache,
	onMarketTermsSaved,
	initialCountry,
}) => {
	const availableMarkets = useMemo(() => markets.filter((market) => market.country), [markets]);
	const [selectedCountry, setSelectedCountry] = useState(availableMarkets[0]?.country || "");
	// Applies `initialCountry` once markets are loaded, then steps aside so the
	// user can freely switch markets afterwards without being overridden.
	const initialCountryAppliedRef = useRef(!initialCountry);
	useEffect(() => {
		if (initialCountryAppliedRef.current) return;
		const match = availableMarkets.find((market) => market.country === initialCountry);
		if (match?.country) {
			setSelectedCountry(match.country);
			initialCountryAppliedRef.current = true;
		}
	}, [availableMarkets, initialCountry]);
	const [versions, setVersions] = useState<CampaignEngagementTerms[]>([]);
	const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [saveInProgress, setSaveInProgress] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [revisionBase, setRevisionBase] = useState<CampaignEngagementTerms | null>(null);
	const validationTargetsRef = useRef(new Map<string, HTMLElement>());
	const form = useEngagementTermsForm();
	const deliverableEditor = useEngagementDeliverables();
	const { hydrate: hydrateForm, setAmount, toSnapshot } = form;
	const { hydrate: hydrateDeliverables } = deliverableEditor;
	const selectedMarket = availableMarkets.find((market) => market.country === selectedCountry);
	const engagementCreators = useEngagementCreators(campaignId, selectedMarket?.blockId, cache);
	const setSelectedCreatorId = engagementCreators.setSelectedId;
	const setVersionsCached = useCallback(
		(updater: CampaignEngagementTerms[] | ((current: CampaignEngagementTerms[]) => CampaignEngagementTerms[])) => {
			setVersions((current) => {
				const next = typeof updater === "function" ? updater(current) : updater;
				if (campaignId && selectedMarket?.blockId && engagementCreators.selectedId) {
					cache.creatorVersions.set(
						getEngagementTermsCreatorKey(campaignId, selectedMarket.blockId, engagementCreators.selectedId),
						next,
					);
				}
				return next;
			});
		},
		[cache, campaignId, engagementCreators.selectedId, selectedMarket?.blockId],
	);

	const hydrateEditor = useCallback(
		(snapshot: CetSnapshot) => {
			hydrateForm(snapshot);
			hydrateDeliverables(snapshot.deliverables);
		},
		[hydrateDeliverables, hydrateForm],
	);
	const termsLifecycle = useEngagementTermsLifecycle({
		campaignId,
		marketBlockId: selectedMarket?.blockId,
		creatorAssignmentId: engagementCreators.selectedId,
		onHydrate: hydrateEditor,
		cache,
	});
	const selectedMarketNeedsSave =
		!!selectedMarket && (!selectedMarket.blockId || selectedMarket.blockId.startsWith("temp-"));

	const isCreatorMode = !!engagementCreators.selectedCreator;
	const selectedVersion = useMemo(
		() => versions.find((version) => version.id === selectedVersionId) || versions[0] || null,
		[selectedVersionId, versions],
	);
	const isLatestVersion = !selectedVersion || selectedVersion.id === versions[0]?.id;
	const creatorTermsLocked = !!selectedVersion && selectedVersion.responseStatus !== "draft";
	const isUnsavedVersion = !!selectedVersion?.id.startsWith(LOCAL_VERSION_PREFIX);
	const currentSnapshot = useMemo(
		() => toSnapshot(deliverableEditor.deliverables),
		[deliverableEditor.deliverables, toSnapshot],
	);
	const hasChanges = form.isDirty(currentSnapshot);
	const deferredCustomizationSnapshot = useDeferredValue(currentSnapshot);
	const previewCustomizedFields = termsLifecycle.previewCustomizedFields;
	const persistedCustomizedFields = termsLifecycle.customizedFields;
	const displayedCustomizedFields = useMemo(
		() => (isCreatorMode ? previewCustomizedFields(deferredCustomizationSnapshot) : persistedCustomizedFields),
		[deferredCustomizationSnapshot, isCreatorMode, persistedCustomizedFields, previewCustomizedFields],
	);
	const showCreatorStatusBadges =
		isCreatorMode && !termsLifecycle.loading && engagementCreators.selectedCreator?.invitationStatus !== "accepted";

	const registerValidationTarget = useCallback<RegisterValidationTarget>(
		(key) => (element) => {
			if (element) validationTargetsRef.current.set(key, element);
			else validationTargetsRef.current.delete(key);
		},
		[],
	);
	const revealValidationTarget = useCallback((key: string) => {
		requestAnimationFrame(() => {
			const target = validationTargetsRef.current.get(key);
			target?.scrollIntoView({ behavior: "smooth", block: "center" });
			target?.querySelector<HTMLElement>("input, button, [tabindex]")?.focus();
		});
	}, []);

	const handleCountryChange = useCallback(
		(country: string) => {
			setSelectedCreatorId(null);
			setSelectedCountry(country);
		},
		[setSelectedCreatorId],
	);
	// Phones open the chosen creator's (or the market's) terms as their own screen; tapping the
	// selected creator again there means "open", not "deselect".
	const isPhone = useMaxWidth(PHONE_MAX_WIDTH);
	const [phoneView, setPhoneView] = useState<"list" | "detail">("list");
	const handleCreatorSelect = useCallback(
		(assignmentId: string) => {
			if (isPhone) {
				setSelectedCreatorId(assignmentId);
				setPhoneView("detail");
				return;
			}
			setSelectedCreatorId((current) => (current === assignmentId ? null : assignmentId));
		},
		[isPhone, setSelectedCreatorId],
	);
	const handleMarketSelect = useCallback(() => {
		setSelectedCreatorId(null);
		if (isPhone) setPhoneView("detail");
	}, [isPhone, setSelectedCreatorId]);
	const showPhoneList = useCallback(() => setPhoneView("list"), []);

	useEffect(() => {
		if (availableMarkets.length && !availableMarkets.some((market) => market.country === selectedCountry)) {
			setSelectedCountry(availableMarkets[0].country || "");
		}
	}, [availableMarkets, selectedCountry]);

	useEffect(() => {
		if (!termsLifecycle.loadError) return;
		console.error("Failed to load engagement terms", termsLifecycle.loadError);
		message.error("Could not load engagement terms for this market");
	}, [termsLifecycle.loadError]);

	useEffect(() => {
		if (!engagementCreators.loadError) return;
		console.error("Failed to load engagement creators", engagementCreators.loadError);
		message.error("Could not load creators for this market");
	}, [engagementCreators.loadError]);

	const hydrateEditorRef = useRef(hydrateEditor);
	useEffect(() => {
		hydrateEditorRef.current = hydrateEditor;
	}, [hydrateEditor]);

	useEffect(() => {
		let cancelled = false;
		const assignmentId = engagementCreators.selectedId;
		if (!campaignId || !selectedMarket?.blockId || !assignmentId) {
			setVersions([]);
			setSelectedVersionId(null);
			setRevisionBase(null);
			setVersionsLoading(false);
			return () => {
				cancelled = true;
			};
		}
		const versionsKey = getEngagementTermsCreatorKey(campaignId, selectedMarket.blockId, assignmentId);
		const cached = cache.creatorVersions.get(versionsKey);
		if (cached) {
			setVersions(cached);
			setSelectedVersionId(cached[0]?.id || null);
			setRevisionBase(null);
			if (cached[0]) hydrateEditorRef.current(snapshotFromResponse(cached[0]));
			return () => {
				cancelled = true;
			};
		}
		if (engagementCreators.selectedCreator?.latestVersionNumber == null) {
			setVersions([]);
			setSelectedVersionId(null);
			setRevisionBase(null);
			setVersionsLoading(false);
			return () => {
				cancelled = true;
			};
		}

		setVersionsLoading(true);
		void loadCachedCreatorVersions(cache, campaignId, selectedMarket.blockId, assignmentId)
			.then((loaded) => {
				if (cancelled) return;
				setVersions(loaded);
				setSelectedVersionId(loaded[0]?.id || null);
				setRevisionBase(null);
				if (loaded[0]) hydrateEditorRef.current(snapshotFromResponse(loaded[0]));
			})
			.catch((error) => {
				if (!cancelled) message.error(getApiErrorMessage(error, "Could not load engagement term versions"));
			})
			.finally(() => {
				if (!cancelled) setVersionsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [
		cache,
		campaignId,
		engagementCreators.selectedCreator?.latestVersionNumber,
		engagementCreators.selectedId,
		selectedMarket?.blockId,
	]);

	useEffect(() => {
		const finalRate = engagementCreators.selectedCreator?.finalRate;
		if (finalRate == null || termsLifecycle.loading || versionsLoading || versions.length > 0) {
			return;
		}
		setAmount(formatBudgetValue(finalRate));
	}, [
		engagementCreators.selectedCreator?.assignmentId,
		engagementCreators.selectedCreator?.finalRate,
		setAmount,
		termsLifecycle.loading,
		versions.length,
		versionsLoading,
	]);

	const validateAndRevealFirstError = (): boolean => {
		const firstFormError = Object.keys(form.validate(isCreatorMode))[0];
		if (firstFormError) {
			revealValidationTarget(firstFormError);
			return false;
		}

		const deliverablesValidation = deliverableEditor.validate();
		if (!deliverablesValidation.valid) {
			if (deliverablesValidation.invalidDeliverable) {
				deliverableEditor.openEditDeliverable(deliverablesValidation.invalidDeliverable, true);
				return false;
			}
			if (deliverablesValidation.firstErrorKey) {
				revealValidationTarget(deliverablesValidation.firstErrorKey);
				return false;
			}
			if (deliverablesValidation.invalidGoLiveDeliverable) {
				deliverableEditor.openGoLiveModal(
					deliverablesValidation.invalidGoLiveDeliverable,
					deliverablesValidation.goLiveValidationError,
				);
			}
			return false;
		}

		if (!termsLifecycle.canPersist) {
			message.error("Save the campaign's market changes before saving engagement terms");
			return false;
		}
		return true;
	};

	const materializeRevision = async (showError = true): Promise<boolean> => {
		if (!isUnsavedVersion) return true;
		if (!campaignId || !selectedMarket?.blockId || !engagementCreators.selectedId) return false;
		try {
			const created = await engagementTermsAPI.issueCreatorTermVersion(
				campaignId,
				selectedMarket.blockId,
				engagementCreators.selectedId,
			);
			setVersionsCached((current) =>
				current.map((version) => (version.id === selectedVersion?.id ? created : version)),
			);
			setSelectedVersionId(created.id);
			termsLifecycle.recordSaved(created);
			setRevisionBase(null);
			return true;
		} catch (error) {
			if (!showError) throw error;
			message.error(getApiErrorMessage(error, "Could not create the new engagement terms version"));
			return false;
		}
	};

	const saveTerms = async (showSuccess = true): Promise<boolean> => {
		if (creatorTermsLocked) {
			message.error("Creator engagement terms are locked");
			return false;
		}
		if (!validateAndRevealFirstError()) return false;
		setSaveInProgress(true);

		try {
			if (!(await materializeRevision())) return false;
			const creatorCustomizedAfterSave = engagementCreators.selectedId
				? previewCustomizedFields(currentSnapshot).length > 0
				: false;
			const saved = await termsLifecycle.save(currentSnapshot);
			hydrateEditor(snapshotFromResponse(saved));
			if (engagementCreators.selectedId) {
				setVersionsCached((current) => {
					const exists = current.some((version) => version.id === saved.id);
					return exists
						? current.map((version) => (version.id === saved.id ? saved : version))
						: [saved, ...current];
				});
				setSelectedVersionId(saved.id);
			}
			if (engagementCreators.selectedId) {
				engagementCreators.setCreatorCustomized(engagementCreators.selectedId, creatorCustomizedAfterSave);
			} else {
				onMarketTermsSaved?.();
				await engagementCreators.refreshCreators();
			}
			if (showSuccess) message.success("Engagement terms saved");
			return true;
		} catch (error) {
			message.error(getApiErrorMessage(error, "Could not save engagement terms"));
			return false;
		} finally {
			setSaveInProgress(false);
		}
	};

	const openSendToCreator = () => {
		if (!engagementCreators.selectedCreator || creatorTermsLocked || !isLatestVersion) return;
		if (!validateAndRevealFirstError()) return;
		engagementCreators.openSendModal(engagementCreators.selectedCreator);
	};

	const confirmSendToCreator = async () => {
		if (!validateAndRevealFirstError() || !selectedMarket?.blockId || !engagementCreators.selectedId) return;
		const marketBlockId = selectedMarket.blockId;
		const creatorId = engagementCreators.selectedId;
		const toastKey = "cet-send";

		setSubmitting(true);
		message.loading({ key: toastKey, content: "Sending engagement terms…", duration: 0 });

		try {
			if (!(await materializeRevision(false))) {
				throw new Error("Could not create the new engagement terms version");
			}
			const customizedFields = termsLifecycle.previewCustomizedFields(currentSnapshot);
			const result = await engagementCreators.sendInvitation({
				marketBlockId,
				engagementTerms: snapshotToPayload(currentSnapshot, customizedFields),
			});
			if (!result) throw new Error("No response received");
			if (result.terms) {
				hydrateEditor(termsLifecycle.recordSaved(result.terms));
				setVersionsCached((current) => {
					const exists = current.some((version) => version.id === result.terms?.id);
					return exists
						? current.map((version) => (version.id === result.terms?.id ? result.terms : version))
						: [result.terms!, ...current];
				});
				setSelectedVersionId(result.terms.id);
				engagementCreators.setCreatorCustomized(creatorId, result.terms.customizedFields.length > 0);
			}
			await engagementCreators.refreshCreators();
			message.success({ key: toastKey, content: "Engagement terms sent to creator" });
		} catch (error) {
			message.error({
				key: toastKey,
				content: getApiErrorMessage(error, "Could not send engagement terms to creator"),
			});
		} finally {
			setSubmitting(false);
		}
	};
	const issueNewVersion = () => {
		if (!campaignId || !selectedMarket?.blockId || !engagementCreators.selectedId || !isLatestVersion) return;
		if (!selectedVersion) return;
		const nextVersion: CampaignEngagementTerms = {
			...selectedVersion,
			id: `${LOCAL_VERSION_PREFIX}${engagementCreators.selectedId}:${selectedVersion.versionNumber + 1}`,
			versionNumber: selectedVersion.versionNumber + 1,
			responseStatus: "draft",
			presentedAt: null,
			respondedAt: null,
			responseReason: null,
			changeSummary: null,
			respondedByName: null,
		};
		setRevisionBase(selectedVersion);
		setVersionsCached((current) => [nextVersion, ...current]);
		setSelectedVersionId(nextVersion.id);
		hydrateEditor(snapshotFromResponse(selectedVersion));
		message.info(`Version ${nextVersion.versionNumber} is ready to edit. Save or send it to create the version.`);
	};

	const selectVersion = (termsId: string) => {
		if (revisionBase && termsId !== selectedVersionId) {
			message.warning("Save or cancel the new version before viewing another version");
			return;
		}
		const version = versions.find((item) => item.id === termsId);
		if (!version) return;
		setSelectedVersionId(version.id);
		hydrateEditor(snapshotFromResponse(version));
	};

	const resetEditor = () => {
		if (isUnsavedVersion && revisionBase) {
			setVersionsCached((current) => current.filter((version) => version.id !== selectedVersion?.id));
			setSelectedVersionId(revisionBase.id);
			hydrateEditor(snapshotFromResponse(revisionBase));
			setRevisionBase(null);
			return;
		}
		const snapshot = form.reset();
		deliverableEditor.hydrate(snapshot.deliverables);
	};

	const cancelSendToCreator = () => {
		engagementCreators.closeSendModal();
		if (isUnsavedVersion) resetEditor();
	};

	if (availableMarkets.length === 0) return <EngagementTermsEmptyState />;

	return (
		<Shell $phoneView={phoneView}>
			{(termsLifecycle.loading || versionsLoading) && (
				<EngagementTermsLoading isLoading label="Loading engagement terms" borderRadius={12}>
					<EngagementTermsLoadingSurface />
				</EngagementTermsLoading>
			)}
			<EngagementCreatorsPanel
				markets={availableMarkets}
				selectedCountry={selectedCountry}
				onCountryChange={handleCountryChange}
				creators={engagementCreators.creators}
				filteredCreators={engagementCreators.filteredCreators}
				selectedId={engagementCreators.selectedId}
				onCreatorSelect={handleCreatorSelect}
				onMarketSelect={handleMarketSelect}
				search={engagementCreators.search}
				onSearchChange={engagementCreators.setSearch}
				loading={engagementCreators.loading}
				marketTermsSaved={termsLifecycle.hasMarketTerms}
			/>

			<FormCard>
				{selectedMarketNeedsSave && (
					<SaveMarketOverlay role="status">
						<SaveMarketMessage>
							<FlagOutlined />
							<Title>Save market first</Title>
							<span>Save the campaign&apos;s market changes before editing engagement terms.</span>
						</SaveMarketMessage>
					</SaveMarketOverlay>
				)}
				<EngagementTermsHeader
					country={selectedCountry}
					creator={engagementCreators.selectedCreator}
					versions={versions}
					selectedVersionId={selectedVersionId}
					onVersionSelect={selectVersion}
					onBack={showPhoneList}
				/>
				<ScrollBody>
					<LockableTerms
						$disabled={creatorTermsLocked || saveInProgress}
						$locked={creatorTermsLocked}
						disabled={creatorTermsLocked || saveInProgress}
						aria-busy={saveInProgress}
					>
						{!isCreatorMode && (
							<InfoBanner>
								<Icon src="/icon/cet-info.svg" size={22} preserveOriginalColor />
								<span>
									Applies to every creator in this market, can be customized individually per creator.
								</span>
							</InfoBanner>
						)}
						<EffectiveDatesSection
							form={form}
							locked={creatorTermsLocked}
							showStatusBadges={showCreatorStatusBadges}
							customizedFields={displayedCustomizedFields}
							registerValidationTarget={registerValidationTarget}
						/>
						<CreatorFeeSection
							form={form}
							isCreatorMode={isCreatorMode}
							showStatusBadges={showCreatorStatusBadges}
							customizedFields={displayedCustomizedFields}
							registerValidationTarget={registerValidationTarget}
						/>
						<DeliverablesSection
							editor={deliverableEditor}
							locked={creatorTermsLocked}
							showStatusBadges={showCreatorStatusBadges}
							customizedFields={displayedCustomizedFields}
							registerValidationTarget={registerValidationTarget}
						/>
						<ExclusivitySection
							form={form}
							showStatusBadges={showCreatorStatusBadges}
							customizedFields={displayedCustomizedFields}
							registerValidationTarget={registerValidationTarget}
						/>
					</LockableTerms>
				</ScrollBody>

				<EngagementTermsFooter
					updatedAt={termsLifecycle.updatedAt}
					locked={creatorTermsLocked}
					dirty={hasChanges}
					loading={termsLifecycle.loading}
					saving={saveInProgress || termsLifecycle.saving}
					submitting={submitting}
					canSend={
						!!engagementCreators.selectedCreator &&
						isLatestVersion &&
						(!selectedVersion || selectedVersion.responseStatus === "draft")
					}
					onReset={resetEditor}
					onSave={() => void saveTerms()}
					onSend={openSendToCreator}
					creatorMode={isCreatorMode}
					version={selectedVersion}
					isLatestVersion={isLatestVersion}
					onIssueVersion={issueNewVersion}
					unsavedVersion={isUnsavedVersion}
				/>
			</FormCard>

			<SendEngagementTermsModal
				open={!!engagementCreators.sendTarget}
				creator={engagementCreators.sendTarget}
				email={engagementCreators.sendEmail}
				message={engagementCreators.sendMessage}
				version={selectedVersion}
				previousVersion={
					selectedVersion
						? versions.find((version) => version.versionNumber === selectedVersion.versionNumber - 1) ||
							null
						: null
				}
				draftSnapshot={currentSnapshot}
				sendReminder={engagementCreators.sendReminder}
				sending={engagementCreators.sending || termsLifecycle.saving}
				onEmailChange={engagementCreators.setSendEmail}
				onMessageChange={engagementCreators.setSendMessage}
				onSendReminderChange={engagementCreators.setSendReminder}
				onCancel={cancelSendToCreator}
				onConfirm={() => void confirmSendToCreator()}
			/>
			<EngagementTermsEditorModals editor={deliverableEditor} />
		</Shell>
	);
};
